import { useState, useEffect } from 'react';
import SiteFooter from '../components/SiteFooter';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { Loader2 } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

// Hardcoded fallback sections (used when API fails or returns empty)
const fallbackSections = [
  {
    title: '1. 定義與解釋',
    content: [
      '「好餸社企」、「我們」指 Delicious Express，一間於香港註冊的社會企業，經營預製菜餸包及生鮮產品網上銷售。',
      '「平台」指我們營運的網站、訂購系統、WhatsApp 溝通渠道及相關數碼服務。',
      '「客戶」、「閣下」指使用本平台上購買產品的任何人士。',
      '「產品」指我們透過平台銷售的預製菜餸包、生鮮食品及其他貨品。',
      '「訂單」指客戶透過平台提交並經我們確認的購買指示。',
    ],
  },
  {
    title: '2. 服務範圍',
    content: [
      '我們提供網上訂購、指定服務屋苑送貨、門市自取及客戶服務支援。',
      '服務範圍以平台不時公佈的服務屋苑名單為準。我們保留隨時調整服務範圍的權利，惟不影響已確認訂單。',
      '派送服務由我們或我們的合作夥伴執行。派送安排、時間及聯絡方式將於訂單確認後透過 WhatsApp 通知客戶。',
    ],
  },
  {
    title: '3. 訂單與付款',
    content: [
      '客戶須於平台選擇產品、服務屋苑或自取方式，填寫姓名、電話、地址及派送時間，並提交訂單。',
      '部分屋苑或自取方式設有最低消費金額，以訂購頁面顯示為準。未達最低消費的訂單將無法提交。',
      '我們接受轉數快（FPS）、銀行轉賬及平台不時公佈的其他付款方式。',
      '客戶付款後須按指示上傳付款憑證。訂單須待我們確認收款後方告成立。我們一般會於收到付款憑證後一個工作天內確認訂單。',
      '我們確認訂單後，將透過 WhatsApp 向客戶發送訂單確認訊息。訂單一經確認，不得取消或更改。',
      '我們保留權利拒絕任何訂單。如拒絕已付款訂單，我們將全數退款。',
      '客戶與我們直接結算。我們負責產品供應、訂單處理、派送安排及客戶服務。',
    ],
  },
  {
    title: '4. 價格與優惠',
    content: [
      '所有價格以港元（HK$）標示，並以下單當日平台顯示的價格為準。我們可隨時調整價格，惟不影響已確認訂單。',
      '優惠券及回贈券設有有效期及最低消費要求；每張訂單僅可使用一張；過期券自動失效，不設補發。',
      '客戶透過專屬推薦連結成功推薦新客戶完成首張訂單，可獲得獎賞。推薦獎賞的資格、金額及發放方式以平台公佈為準。我們保留權利取消涉及自我推薦、虛假賬戶或濫用行為的獎賞。',
    ],
  },
  {
    title: '5. 派送與自取',
    content: [
      '客戶可於落單時選擇派送日期及時段。每日設有截單時間，截單後提交的訂單將順延一日處理，以訂購頁面註明為準。',
      '我們及合作夥伴將盡力按時派送，惟交通、天氣（包括八號或以上颱風信號、黑色暴雨警告）或其他不可預見情況可能導致延誤。如因上述情況延誤，我們將盡快通知客戶並重新安排派送，惟不承擔延誤引致的間接損失。',
      '產品送達後，客戶或其代表應立即檢查。如發現產品錯漏或明顯損壞，應於收貨後 24 小時內透過 WhatsApp 聯絡我們並提供相片。',
      '選擇自取的客戶須於指定時間及地點取貨。逾期未取且未作聯絡者，訂單將視作完成處理，貨品因變質而被棄置，已付款項不予退還。',
      '如因客戶提供錯誤地址、無人收貨或客戶無法聯絡而導致派送失敗，我們將安排再次派送或轉為自取，惟可能收取額外派送費用；如產品因客戶原因變質，恕不退款。',
    ],
  },
  {
    title: '6. 產品責任與食品安全',
    content: [
      '預製餸包均標示食用期限及儲存方法。客戶須按包裝指示冷藏（一般為攝氏 0-4 度）並於期限內食用。',
      '產品一經送達或領取，儲存及食用責任即轉移至客戶。因客戶儲存不當引致的品質問題，我們概不負責。',
      '產品成分資料載於產品頁面及包裝。我們的廚房同時處理含麩質、甲殼類、蛋類、魚類、花生、大豆、奶類及木本堅果的食材。對食物過敏的客戶，落單前應細閱成分資料或向我們查詢。',
      '如產品於送達時已變質、包裝破損或與訂單明顯不符，客戶可於收貨後 24 小時內提出，經核實後我們將安排退款或重製補送。因個人口味偏好提出的意見，不屬退換範圍，惟我們會認真記錄以改進產品。',
      '基於食品安全，所有已送達或已領取的食品，除上述品質問題外，不設退貨或換貨。',
    ],
  },
  {
    title: '7. 用戶行為規範',
    content: [
      '閣下承諾不會提交虛假訂單、使用他人身份或付款資料；上傳偽造付款憑證；濫用推薦系統；騷擾、辱罵或威脅我們的員工或合作夥伴；干擾平台正常運作；或利用平台從事任何違法活動。',
      '違反上述規範者，我們有權取消其訂單、凍結其賬戶及獎賞，並保留法律追究權利。',
    ],
  },
  {
    title: '8. 免責聲明',
    content: [
      '因天災、疫症、政府行為、罷工、公用服務中斷或其他我們無法合理控制的事件導致服務延誤或無法履行，我們概不承擔責任。',
      '我們致力維持平台穩定運作，惟不保證平台永不中斷或完全無誤。因系統故障、維護或第三方服務（包括 Cloudflare、WhatsApp/Meta）中斷引致的損失，我們在法律允許的最大範圍內不承擔責任。',
      '平台可能載有第三方網站連結，該等網站的內容及私隱做法與我們無關，我們概不負責。',
      '除法律不可排除的責任外，我們就任何索償的累計賠償責任，以引致索償的相關訂單金額為上限。我們概不就任何間接、附帶或後果性損失（包括利潤損失）承擔責任。',
    ],
  },
  {
    title: '9. 條款修改與終止',
    content: [
      '我們可不時修訂本條款，修訂後的版本將刊載於平台並註明更新日期。重大修改將透過 WhatsApp 或平台公告另行通知。閣下於修訂後繼續使用服務，即視為接受修訂後的條款。',
      '閣下可隨時停止使用本服務。我們亦可因閣下違反本條款而終止向閣下提供服務。',
      '本條款中有關食品安全、免責、爭議解決的條文，於服務終止後仍然有效。',
    ],
  },
  {
    title: '10. 適用法律與爭議解決',
    content: [
      '本條款受香港特別行政區法律管轄，並按其詮釋。',
      '因本條款或本服務引起的任何爭議，雙方應首先以誠意協商解決；協商不成的，任何一方均可將爭議提交香港法院排他性司法管轄。',
      '如本條款任何條文被裁定無效或不可執行，其餘條文的效力不受影響。',
    ],
  },
];

function parseSections(rawText: string) {
  if (!rawText.trim()) return null;
  const lines = rawText.split('\n').filter(l => l.trim());
  const sections: { title: string; content: string[] }[] = [];
  let current: { title: string; content: string[] } | null = null;
  for (const line of lines) {
    const trimmed = line.trim();
    if (/^\d+\./.test(trimmed) || /^第.*/.test(trimmed) || trimmed.endsWith('：') || trimmed.endsWith(':')) {
      if (current) sections.push(current);
      current = { title: trimmed, content: [] };
    } else if (current) {
      current.content.push(trimmed);
    }
  }
  if (current) sections.push(current);
  return sections.length > 0 ? sections : null;
}

export default function TermsOfService() {
  const { t, i18n } = useTranslation();
  const [sections, setSections] = useState(fallbackSections);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/public/settings/terms')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.terms_of_service) {
          const parsed = parseSections(data.terms_of_service);
          if (parsed) setSections(parsed);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // EN 模式顯示 en/content.json 的法律正文鏡像；缺鏡像時回退 API/fallback 內容（zh 恆用 API/fallback）
  const isEn = i18n.language === 'en';
  const mirror = isEn
    ? t('content:legal.terms.sections', { returnObjects: true }) as unknown
    : null;
  const displaySections =
    mirror && Array.isArray(mirror) && mirror.length > 0
      ? (mirror as { title: string; content: string[] }[])
      : sections;

  return (
    <>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8 transition-colors">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 md:p-10 transition-colors">
          {/* 本頁無 SiteHeader，自帶語言切換器 */}
          <div className="flex justify-end mb-2">
            <LanguageSwitcher size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('content:legal.heading1')}
          </h1>
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
            {t('content:legal.terms.heading2')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            {t('content:legal.terms.lastUpdated')}
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-orange-600" size={32} />
            </div>
          ) : (
            <div className="space-y-8">
              {displaySections.map((section) => (
                <section key={section.title}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {section.title}
                  </h3>
                  <div className="space-y-3 text-gray-700 dark:text-gray-300 leading-relaxed">
                    {section.content.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('content:legal.terms.contactNote')}
            </p>
            <Link
              to="/"
              className="inline-flex items-center text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
            >
              {t('content:legal.backHome')}
            </Link>
          </div>
        </div>
      </div>
    </div>
    <SiteFooter />
    </>
  );
}
